package ai.jessie.web.onboarding

import ai.jessie.shared.onboarding.EntryDestination
import ai.jessie.shared.onboarding.OrganisationChoiceResponse
import ai.jessie.web.theme.HintStyle
import ai.jessie.web.theme.RowStyle
import ai.jessie.web.theme.StackStyle
import ai.jessie.web.theme.themedButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.document
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.H2
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Section
import org.jetbrains.compose.web.dom.Text
import org.w3c.dom.HTMLElement

@Composable
fun authenticatedEntry() {
    val port = LocalOnboardingPort.current
    val scope = rememberCoroutineScope()
    val entry = remember(port, scope) { EntryState(port, scope) }
    LaunchedEffect(entry) { if (!AppGlobals.isExporting) entry.refresh() }
    followEntry(entry.state.entry)
    onboardingAlert(entry.state.error, entry.state.busy)
    if (entry.state.error != null)
        P {
            actionButton(
                "Organisationszugriff erneut prüfen",
                entry.state.busy,
                entry.actions.refresh,
            )
        }
}

@Composable
fun organisationSelector(state: EntryUiState, actions: EntryActions) {
    onboardingAlert(state.error, state.busy)
    if (state.error?.authenticationRequired == true) return
    if (state.busy) busyStatus("Organisationszugriff wird geprüft …")
    when (state.entry?.destination) {
        EntryDestination.NO_MEMBERSHIP ->
            P(attrs = HintStyle.toAttrs()) {
                Text(
                    "Zurzeit hast du keinen Organisationszugriff. Bitte kontaktiere den Support oder melde dich ab."
                )
            }
        EntryDestination.SELECT_ORGANISATION -> {
            P { Text("Wähle die Organisation, in der du arbeiten möchtest.") }
            Div(attrs = StackStyle.toAttrs()) {
                state.entry.organisations.forEach { choice ->
                    Form(
                        attrs = {
                            addEventListener("submit") {
                                it.preventDefault()
                                actions.select(choice)
                            }
                        }
                    ) {
                        themedButton(
                            "${choice.name} · ${roleLabel(choice.role)} — Öffnen",
                            {},
                            disabled = state.busy,
                            submit = true,
                        )
                    }
                }
            }
        }
        else -> Unit
    }
    // UC-026 A2: the global profile stays reachable without any organisation.
    P { A(href = "/profile") { Text("Mein Profil") } }
    P { actionButton("Zugriff aktualisieren", state.busy, actions.refresh) }
}

@Composable
fun organisationSwitch(
    activeOrganisationId: String,
    state: OrganisationSwitchUiState,
    actions: OrganisationSwitchActions,
) {
    val focusReturn = remember { FocusReturn() }
    if (state.activeRevoked)
        P(attrs = HintStyle.toAttrs()) {
            Text(
                "Dein Zugriff auf diese Organisation ist nicht mehr verfügbar. " +
                    "Es werden keine Organisationsdaten angezeigt."
            )
        }
    if (!state.open) {
        if (state.error?.authenticationRequired == true) onboardingAlert(state.error, state.busy)
        else P { actionButton("Organisation wechseln", state.busy, actions.open, "switch-open") }
        LaunchedEffect(Unit) {
            if (focusReturn.pending && !AppGlobals.isExporting)
                (document.getElementById("switch-open") as? HTMLElement)?.focus()
            focusReturn.pending = false
        }
        return
    }
    Section(attrs = { attr("aria-labelledby", "switch-title") }) {
        H2(
            attrs = {
                id("switch-title")
                attr("tabindex", "-1")
            }
        ) {
            Text("Organisation wechseln")
        }
        onboardingAlert(state.error, state.busy)
        if (state.error?.authenticationRequired == true) return@Section
        if (state.busy) busyStatus("Organisationszugriff wird geprüft …")
        val pending = state.pendingDiscard
        if (pending != null) {
            discardConfirmation(pending, state.busy, actions)
            return@Section
        }
        state.choices?.let { switchChoices(activeOrganisationId, it, state.busy, actions) }
        Div(attrs = RowStyle.toAttrs()) {
            actionButton("Zugriff aktualisieren", state.busy, actions.refresh)
            actionButton("Abbrechen", state.busy, actions.cancel)
        }
    }
    LaunchedEffect(Unit) {
        focusReturn.pending = true
        if (!AppGlobals.isExporting)
            (document.getElementById("switch-title") as? HTMLElement)?.focus()
    }
}

@Composable
private fun switchChoices(
    activeOrganisationId: String,
    choices: List<OrganisationChoiceResponse>,
    busy: Boolean,
    actions: OrganisationSwitchActions,
) {
    if (choices.isEmpty())
        P(attrs = HintStyle.toAttrs()) {
            Text(
                "Zurzeit hast du keinen Organisationszugriff. Bitte kontaktiere den Support oder melde dich ab."
            )
        }
    Div(attrs = StackStyle.toAttrs()) {
        choices.forEach { choice ->
            if (choice.organisationId.equals(activeOrganisationId, ignoreCase = true))
                P { Text("${choice.name} · ${roleLabel(choice.role)} — aktiv") }
            else
                P {
                    actionButton(
                        "${choice.name} · ${roleLabel(choice.role)} — Wechseln",
                        busy,
                        { actions.choose(choice) },
                    )
                }
        }
    }
}

// Returns keyboard focus to the opener after cancel or keep, not on first page render.
private class FocusReturn {
    var pending = false
}

@Composable
private fun discardConfirmation(
    pending: OrganisationChoiceResponse,
    busy: Boolean,
    actions: OrganisationSwitchActions,
) {
    Div(
        attrs =
            HintStyle.toAttrs {
                id("discard-confirmation")
                attr("role", "alertdialog")
                attr("aria-labelledby", "discard-message")
                attr("tabindex", "-1")
            }
    ) {
        P(attrs = { id("discard-message") }) {
            Text("Beim Wechsel zu ${pending.name} gehen deine ungespeicherten Änderungen verloren.")
        }
        Div(attrs = RowStyle.toAttrs()) {
            actionButton("Änderungen verwerfen und wechseln", busy, actions.confirmDiscard)
            actionButton("Änderungen behalten", busy, actions.keepChanges)
        }
    }
    LaunchedEffect(pending) {
        if (!AppGlobals.isExporting)
            (document.getElementById("discard-confirmation") as? HTMLElement)?.focus()
    }
}

// UC-008, UC-009, UC-013 and UC-017 visibility only; every work, calendar, invoice and document
// request is authorised by the server (BR-072).
@Composable
private fun workLinks(organisationId: String) {
    P { A(href = "/organisations/$organisationId/work") { Text("Arbeit") } }
    P { A(href = "/organisations/$organisationId/calendar") { Text("Kalender") } }
    invoiceLink(organisationId)
    documentsLink(organisationId)
}

@Composable
private fun invoiceLink(organisationId: String) {
    P { A(href = "/organisations/$organisationId/invoices") { Text("Rechnungen") } }
}

// UC-015 B6 and UC-017 B4 for accountants; the server lists and opens issued invoices alone.
@Composable
private fun accountantLinks(organisationId: String) {
    invoiceLink(organisationId)
    documentsLink(organisationId)
}

// UC-017 B4 visibility only (all three roles); every document request is authorised by the server.
@Composable
private fun documentsLink(organisationId: String) {
    P { A(href = "/organisations/$organisationId/documents") { Text("Dokumente") } }
}

@Composable
fun organisationStart(state: OrganisationStartUiState, refresh: () -> Unit) {
    onboardingAlert(state.error, state.busy)
    if (state.error?.authenticationRequired == true) return
    if (state.busy) busyStatus("Organisation wird geladen …")
    state.data?.let { data ->
        H2 { Text(data.name) }
        P { Text("Deine Rolle: ${roleLabel(data.role)}") }
        // UC-026: own profile with this membership selected; the server rechecks the membership.
        P { A(href = "/profile?organisationId=${data.organisationId}") { Text("Mein Profil") } }
        // UC-005 entry. Presentation only: the server authorises every member-management request.
        if (data.role == "OWNER")
            P {
                A(href = "/organisations/${data.organisationId}/members") {
                    Text("Mitglieder verwalten")
                }
            }
        // UC-025 visibility only; every settings request is authorised by the server.
        if (data.role in listOf("OWNER", "ACCOUNTANT"))
            P {
                A(href = "/organisations/${data.organisationId}/settings") {
                    Text("Organisationseinstellungen")
                }
            }
        // UC-012 visibility only; every customer request is authorised by the server.
        if (data.role in listOf("OWNER", "STANDARD"))
            P { A(href = "/organisations/${data.organisationId}/customers") { Text("Kunden") } }
        // UC-010 visibility only; every project request is authorised by the server.
        if (data.role in listOf("OWNER", "STANDARD"))
            P { A(href = "/organisations/${data.organisationId}/projects") { Text("Projekte") } }
        if (data.role in listOf("OWNER", "STANDARD")) workLinks(data.organisationId)
        if (data.role == "ACCOUNTANT")
            P {
                A(href = "/organisations/${data.organisationId}/customer-billing") {
                    Text("Rechnungsangaben der Kunden")
                }
            }
        // UC-015 B6 and UC-017 B4 visibility only.
        if (data.role == "ACCOUNTANT") accountantLinks(data.organisationId)
        if (data.missingBusinessSettings) {
            H2 { Text("Geschäftseinstellungen fehlen") }
            P {
                Text(
                    "Adresse, Land sowie Rechnungs- und Dokumenteinstellungen müssen vor der Nutzung " +
                        "abhängiger Funktionen vollständig eingerichtet sein."
                )
            }
            P {
                Text(
                    if (data.role == "OWNER")
                        "Bitte vervollständige die Organisationseinstellungen. " +
                            "Dein Organisationszugriff bleibt verfügbar."
                    else
                        "Bitte kontaktiere einen Eigentümer deiner Organisation. " +
                            "Dein Organisationszugriff bleibt verfügbar."
                )
            }
            if (data.role == "OWNER")
                P {
                    A(href = "/organisations/${data.organisationId}/settings") {
                        Text("Geschäftseinstellungen einrichten")
                    }
                }
        }
    }
    P { actionButton("Organisation erneut laden", state.busy, refresh) }
}
